



































































































































































































import {
  useCartStore as useCheckoutCartStore,
  useUserStore as useCheckoutUserStore
} from '@checkout/stores';
import type { Ref } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  ssrRef,
  useContext,
  useFetch,
  useMeta,
  useRouter,
  watch
} from '@nuxtjs/composition-api';
import { SfHeading } from '@storefront-ui/vue';
import NostoCart from 'atoms/NostoCart.vue';
import debounce from 'lodash.debounce';
import Totals from 'molecules/Totals.vue';
import VaimoCollectedProducts from 'organisms/cart/parts/VaimoCollectedProducts.vue';

import CouponCode from '~/components/CouponCode.vue';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import type { Cart } from '~/composables';
import { useConfig, useStore, useUiNotification } from '~/composables';
import VaimoLoader from '~/diptyqueTheme/components/atoms/VaimoLoader.vue';
import CartPreview from '~/diptyqueTheme/components/molecules/CartPreview.vue';
import VaimoSpecialOfferProducts from '~/diptyqueTheme/components/organisms/cart/parts/VaimoSpecialOfferProducts.vue';
import { useGoogleTagManager, useNarVar } from '~/diptyqueTheme/composable';
import { useBooxi } from '~/diptyqueTheme/composable/useBooxi';
import contentfulEntriesIds from '~/diptyqueTheme/config/contentfulEntries';
import { getOfferProducts } from '~/diptyqueTheme/getters/offerProductsGetter';
import { validateFraction } from '~/diptyqueTheme/helpers/priceFormatter';
import { useXmas } from '~/diptyqueTheme/modules/xmas/composables/useXmas';
import { useCountriesStore } from '~/diptyqueTheme/stores/countries';
import { useContentfulGQL } from '~/integrations/contentful/src/composables/useContentfulGQL';
import type { Cart as CheckoutCart, User as CheckoutUser } from '~/models';
import useCart from '~/modules/core/composables/useCart';
import cartGetters from '~/modules/core/getters/cartGetters';
import { useCartStore } from '~/modules/core/stores/cart';
import useUser from '~/modules/customer/composables/useUser';

export default defineComponent({
  name: 'CartPage',
  components: {
    SkeletonLoader,
    CartPreview,
    SfHeading,
    VaimoCollectedProducts,
    VaimoSpecialOfferProducts,
    Totals,
    NostoCart,
    CouponCode,
    VaimoLoader,
    AdyenPayPalExpressCheckout: () =>
      import('~/integrations/adyen/components/AdyenPaypalExpressCheckout.vue'),
    UpsellProducts: () => import('templates/sections/ProductSlider/index.vue'),
    SampleProducts: () => import('organisms/cart/parts/SampleProducts.vue'),
    VaimoButton: () => import('atoms/VaimoButton.vue'),
    AdyenApplePayPeymentProvider: () =>
      import(
        '~/integrations/adyen/components/AdyenApplePayPaymentProvider.vue'
      ),
    EngravingLegalNotice: () =>
      import('organisms/product/EngravingLegalNotice.vue'),
    VaimoGiftWrapping: () =>
      import(
        '~/diptyqueTheme/components/organisms/VaimoGiftWrapping/VaimoGiftWrapping.vue'
      ),
    VaimoCartGiftMessage: () =>
      import(
        '~/diptyqueTheme/components/organisms/VaimoGiftMessage/VaimoCartGiftMessage.vue'
      )
  },
  scrollToTop: true,
  setup() {
    const { i18n, app, route } = useContext();
    const { sendBooxiExpiredMessage, checkIsBooxiExpired } = useBooxi();
    const { query, queryResponse } = useContentfulGQL('byIdUpSellProducts');
    const {
      cart,
      removeItem,
      updateItemQty,
      loading,
      load,
      loadCartStockStatus
    } = useCart();
    const { user, loading: userLoading } = useUser();
    const checkoutCartStore = useCheckoutCartStore();
    const checkoutUserStore = useCheckoutUserStore();
    const products = computed(() => cartGetters.getItems(cart.value));
    const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
    const UPDATE_QUANTITY_DEBOUNCE = 500;
    const updateQuantity = debounce(async ({ product, quantity }) => {
      await updateItemQty({ product, quantity });
    }, UPDATE_QUANTITY_DEBOUNCE);
    const upSellProductsData = ssrRef(null);
    const {
      getEstimatedDeliveryDate,
      estimatedDeliveryDate,
      formatDate,
      loading: estimateDeliveryDateLoading
    } = useNarVar();
    const { getShowCartpageDetails } = useGoogleTagManager();
    const showCartGiftMessage = ref(false);
    const { getAllowedCountries, loadCountriesList } = useCountriesStore();
    const router = useRouter();
    const countriesLoading = ref(false);
    const upsellProductsLoading: Ref<boolean> = ref(false);

    const isExpressCheckoutPopupOpen = ref(false);
    const { updateQty: xmasUpdateBagsQty } = useXmas();
    const { addBooxiProductToCart } = useBooxi();
    const { booking: booxiData } = route.value.query;
    const cartStore = useCartStore();
    const { hasOneBooxiItemInCart } = useBooxi();
    const { config } = useConfig();
    const { isUsCaStore, isJpStore } = useStore();

    const isXmasEnabled = computed(() => config.value.xmas_enable);
    const isDataLoading = computed(() => cartStore.is_data_loading);
    const cartInitialLoading = computed(() => cartStore.initial_loading);

    const { send: sendNotification } = useUiNotification();

    const transformUpsellsData = (data) => {
      if (!data || !data.productGroup) return null;
      return {
        heading: data.heading,
        productGroupsCollection: {
          items: [data.productGroup]
        }
      };
    };

    const toggleVariationsPopup = (): void => {
      isExpressCheckoutPopupOpen.value = !isExpressCheckoutPopupOpen.value;
    };
    const isDesktop = computed(() => app.$device.isDesktop);

    const isUserOrDataLoading = computed(() => {
      return userLoading.value || isDataLoading.value;
    });

    const applePayMainButtonAdditionalClasses = 'main-applepay';

    onMounted(async () => {
      const setUpsellItem = () => {
        const upsellItem =
          queryResponse.value?.collectionPushCollection?.items?.[0];
        if (upsellItem) {
          upSellProductsData.value = transformUpsellsData(upsellItem);
        }
      };

      const apiState = app.context.$vsf.$magento.config.state;
      const currentCartId = apiState.getCartId();

      const processCartData = async (cartId: string) => {
        if (booxiData) {
          await addBooxiProductToCart(booxiData as string);
        }

        await Promise.all([
          getEstimatedDeliveryDate(cartId),
          loadCartStockStatus({ cartId })
        ]);

        setUpsellItem();
        upsellProductsLoading.value = false;

        nextTick(() => {
          window.scroll(0, 0);
          pushCartViewOnLoad();
        });
      };

      if (!isDataLoading.value) {
        await load();
      }

      if (currentCartId && !isDataLoading.value) {
        await processCartData(currentCartId);
      } else {
        const stopWatchingCartId = watch(
          () => cart.value?.id,
          async (newCartId) => {
            if (!newCartId) return;
            stopWatchingCartId();
            await processCartData(newCartId);
          },
          { immediate: false }
        );
      }
    });

    const pushCartViewEvent = (cartData: Cart) =>
      app.$gtm.push(getShowCartpageDetails(cartData));

    const pushCartViewOnLoad = () => {
      if (!cartStore.is_cart_loading) {
        pushCartViewEvent(cart.value);
      } else {
        const stop = watch(
          () => cartStore.is_cart_loading,
          (isStillLoading) => {
            if (isStillLoading) return;

            pushCartViewEvent(cart.value);
            stop?.();
          }
        );
      }
    };

    useFetch(async () => {
      upsellProductsLoading.value = true;
      await query('getUpsellProductsByKey', {
        key: contentfulEntriesIds.CART_UPSELL_PRODUCTS_KEY,
        locale: i18n.localeProperties.contentfulLocale
      });

      countriesLoading.value = true;
      await Promise.all([getAllowedCountries(), loadCountriesList()]);
      countriesLoading.value = false;
    });

    const getEstimatedDeliveryDataText = computed(() => {
      if (estimatedDeliveryDate.value) {
        return i18n.t('Order today and receive your items by {date}', {
          date: formatDate(estimatedDeliveryDate.value, true, true)
        });
      }
    });

    const hasSpecialOffers = computed(() => {
      return totalItems.value && getOfferProducts(cart.value).length;
    });

    const shippingMessage = computed(() => {
      if (isUsCaStore.value) {
        return i18n.t(
          'Tax and shipping costs will be calculated according to your delivery address.'
        );
      }
      return i18n.t('Shipping costs will be calculated in the next step.');
    });

    const toggleCartGiftMessage = async (giftWrapping) => {
      showCartGiftMessage.value = giftWrapping && !giftWrapping?.is_default;
      if (!showCartGiftMessage.value && isXmasEnabled.value) {
        await xmasUpdateBagsQty(0);
      }
    };

    const goToCheckout = async () => {
      if (hasOneBooxiItemInCart.value && checkIsBooxiExpired()) {
        sendBooxiExpiredMessage();
        return;
      }
      if (countriesLoading.value) {
        //dont allow proceeding to checkout until countries are finished loading
        return;
      }

      window.scrollTo(0, 0);

      const url = user.value ? '/checkout#shipping' : '/checkout#user-account';
      checkoutCartStore.setCart(cart.value as unknown as CheckoutCart);
      checkoutUserStore.setUser(user.value as unknown as CheckoutUser);
      await router.replace(app.localePath(url));
    };

    const metaTags = computed(() => ({
      bodyAttrs: {
        class: ['cart-page']
      },
      title: `${i18n.t('Cart')} | Diptyque Paris`,
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: `${i18n.t('Cart')} | Diptyque Paris`
        },
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex, nofollow'
        }
      ]
    }));

    useMeta(metaTags.value);

    watch(
      () => cartStore.cartStockStatus?.items,
      (newItems) => {
        if (!newItems) return;

        const outOfStockItems = newItems.find(
          (item) => item?.product?.stock_status === 'OUT_OF_STOCK'
        );

        if (outOfStockItems) {
          sendNotification({
            id: Symbol('has_out_of_stock_items'),
            message: i18n.t('Some of the products are out of stock.') as string,
            persist: false,
            type: 'danger',
            icon: 'cross'
          });
        }
      },
      { immediate: true, deep: true }
    );

    const isPaypalExpressCheckoutEnabled = computed(() => {
      return (
        !app.$device.isSafari && config.value?.adyen_express_paypal_enabled
      );
    });

    const isAppleExpressCheckoutEnabled = computed(() => {
      return app.$device.isSafari && config.value?.is_apple_pay_enabled;
    });

    const isAnyExpressCheckoutEnabled = computed(
      () =>
        isPaypalExpressCheckoutEnabled.value ||
        isAppleExpressCheckoutEnabled.value
    );

    const showApplePayOnMobile = computed(
      () => !isDesktop.value && isAppleExpressCheckoutEnabled.value
    );

    const showApplePayOnDesktop = computed(
      () => isDesktop.value && isAppleExpressCheckoutEnabled.value
    );

    const showPaypalOnMobile = computed(
      () => !isDesktop.value && isPaypalExpressCheckoutEnabled.value
    );

    const showPaypalOnDesktop = computed(
      () => isDesktop.value && isPaypalExpressCheckoutEnabled.value
    );

    return {
      showPaypalOnDesktop,
      showPaypalOnMobile,
      showApplePayOnDesktop,
      showApplePayOnMobile,
      isPaypalExpressCheckoutEnabled,
      isAppleExpressCheckoutEnabled,
      isAnyExpressCheckoutEnabled,
      cart,
      cartInitialLoading,
      removeItem,
      getEstimatedDeliveryDataText,
      updateItemQty,
      upSellProductsData,
      loading,
      upsellProductsLoading,
      estimateDeliveryDateLoading,
      products,
      totalItems,
      hasSpecialOffers,
      shippingMessage,
      updateQuantity,
      cartGetters,
      validateFraction,
      showCartGiftMessage,
      toggleCartGiftMessage,
      countriesLoading,
      goToCheckout,
      applePayMainButtonAdditionalClasses,
      toggleVariationsPopup,
      isExpressCheckoutPopupOpen,
      isDesktop,
      hasOneBooxiItemInCart,
      isBooxiLoading: computed(() => cartStore.is_booxi_loading),
      isDataLoading,
      isJpStore,
      isUserOrDataLoading
    };
  },
  head: {}
});
